import type { Option } from "@tento-lona/sheets";
import { Tracing } from "@tento-core/tracing";
import type { SheetDelegate } from "../sheet-delegate.ts";
import type { SubcolumnMenuPresenter } from "./menu-presenter.ts";

/**
 * SubcolumnMenuPresenter for column-based layouts (Calendar daily, Timeline).
 *
 * Delegates to SheetDelegate.PopoverHandle and FixedChildHandle, which
 * provide scroll-aware positioning anchored to sheet columns.
 */
export class ColumnMenuPresenter implements SubcolumnMenuPresenter {
  readonly #fixedChild: SheetDelegate.FixedChildHandle;
  readonly #popover: SheetDelegate.PopoverHandle;
  constructor(
    popover: SheetDelegate.PopoverHandle,
    fixedChild: SheetDelegate.FixedChildHandle,
  ) {
    this.#popover = popover;
    this.#fixedChild = fixedChild;
  }

  surface(
    position: SubcolumnMenuPresenter.Position,
  ): Option<SheetDelegate.SurfaceLocation> {
    if (!("column" in position)) return null;
    return this.#popover.surface({ x: position.column, y: position.rowId });
  }

  showPopover(
    id: string,
    $content: HTMLElement,
    position: SubcolumnMenuPresenter.Position,
    options: Option<SubcolumnMenuPresenter.PopoverOptions>,
  ): void {
    const pos = position as SubcolumnMenuPresenter.ColumnPosition;
    Tracing.log("presenter/show-popover", { id, rowId: String(pos.rowId), offsetY: options?.offsetY });
    this.#popover.reveal(
      id,
      $content,
      { x: pos.column, y: pos.rowId },
      options,
    );
  }

  showFixedChild(
    id: string,
    $content: HTMLElement,
    position: SubcolumnMenuPresenter.Position,
    inset: { left: number; top: number },
    options: Option<SubcolumnMenuPresenter.FixedChildOptions>,
  ): void {
    const pos = position as SubcolumnMenuPresenter.ColumnPosition;
    Tracing.log("presenter/show-fixed-child", { id, rowId: String(pos.rowId), insetLeft: inset.left, insetTop: inset.top });
    this.#fixedChild.reveal(id, $content, { x: pos.column, y: pos.rowId }, {
      inset,
      widthBehavior: options?.widthBehavior ?? "auto",
    });
  }

  dismiss(id: string): void {
    this.#popover.dismiss(id);
    this.#fixedChild.dismiss(id, "instant");
  }

  isShowing(id: string): boolean {
    return this.#popover.currentId() === id || this.#fixedChild.hasFixedChild(id);
  }
}
